/**
 * Modal para editar subcategorías existentes.
 *
 * Este componente proporciona una interfaz de usuario para editar subcategorías
 * existentes, incluyendo campos de nombre y estado (activo/inactivo). Incluye
 * validación en tiempo real, detección de cambios y formateo automático de texto.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Icon,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  Card,
  FormHelperText,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

/**
 * Modal para editar subcategorías existentes
 * @param {Object} props - Propiedades del componente
 */
export function SubCategoryEdit({
  open,
  handleClose,
  datosRecuperados,
  datosEditados,
  isFormModified,
  isDisabled,
  getFieldValue,
  handleFieldChange,
  handleSubmit,
  isFieldValid,
}) {
  const [isFormValid, setIsFormValid] = useState(true);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "400px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
  };

  // Validación de nombres permitiendo tildes y caracteres especiales comunes
  const validateText = (text) => /^[a-zA-ZÀ-ÿ\s\u00f1\u00d1,;.:()\-]*$/.test(text);

  const capitalizeWords = (str) => {
    const value = str;
    const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);
    return formattedValue;
  };

  // Validar todos los campos necesarios para habilitar el botón de actualización
  useEffect(() => {
    const validateForm = () => {
      const name = getFieldValue("name");
      const isEnabled = getFieldValue("isEnabled");

      return validateText(name) && name?.trim() !== "" && isEnabled !== undefined;
    };

    setIsFormValid(validateForm());
  }, [getFieldValue, isFormModified]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Typography id="modal-modal-title" component="h2">
            Editando subcategoría: {datosRecuperados.name || ""}
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={handleClose}>
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <Typography id="modal-modal-title" variant="h5" component="h2">
          Nombre
        </Typography>
        <MDInput
          disabled={isDisabled}
          style={{ marginTop: "5px" }}
          value={getFieldValue("name") || ""}
          error={!isFieldValid.name}
          helperText={
            getFieldValue("name") === "" && !isFieldValid.name
              ? "El campo no puede estar vacío"
              : !isFieldValid.name
                ? "No se permiten números ni caracteres especiales"
                : ""
          }
          FormHelperTextProps={{ className: !isFieldValid.name ? "Mui-error" : "" }}
          onChange={(e) => handleFieldChange("name", capitalizeWords(e.target.value))}
        />
        <br />
        <form onSubmit={handleSubmit}>
          <Box display="flex" alignItems="flex-start">
            <Box>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                Estado
              </Typography>
              <FormControl error={!isFieldValid.isEnabled}>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={getFieldValue("isEnabled") === true ? "true" : "false"}
                  onChange={(e) => handleFieldChange("isEnabled", e.target.value === "true")}>
                  <FormControlLabel
                    disabled={isDisabled}
                    value="true"
                    control={<Radio />}
                    label="Activo"
                  />
                  <FormControlLabel
                    disabled={isDisabled}
                    value="false"
                    control={<Radio />}
                    label="Inactivo"
                  />
                </RadioGroup>
                {!isFieldValid.isEnabled && <FormHelperText>Estado requerido</FormHelperText>}
              </FormControl>
            </Box>
          </Box>
          <Divider variant="middle" />
          <Box
            style={{
              display: "flex",
              gap: "5px",
              justifyContent: "flex-end",
            }}>
            <MDButton onClick={handleClose} color="error">
              Cancelar
            </MDButton>
            <MDButton type="submit" disabled={!isFormModified || !isFormValid} color="success">
              Actualizar
            </MDButton>
          </Box>
        </form>
      </Card>
    </Modal>
  );
}

SubCategoryEdit.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  datosRecuperados: PropTypes.object,
  isDisabled: PropTypes.bool,
  datosEditados: PropTypes.object,
  getFieldValue: PropTypes.func,
  handleFieldChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  isFormModified: PropTypes.bool,
  isFieldValid: PropTypes.object,
};
